import React, { useState, useEffect } from 'react';
import { useBrokerIntegration } from '../../contexts/BrokerIntegrationContextCompat';
import { FiPlus, FiTrash2, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiExternalLink } from 'react-icons/fi';
import useBrokerOAuthStore from '../../stores/useBrokerOAuthStore';
import { useNavigate } from 'react-router-dom';
import BrokerStatusBadge from './BrokerStatusBadge';
import BrokerConnectionWarningModal from './BrokerConnectionWarningModal';

import brokerFactory from '../../services/brokers/brokerFactory';
import unifiedOAuthService from '../../services/brokers/unifiedOAuthService';


const BrokerIntegrationManager = () => {
  const {
    brokerIntegrations,
    activeConnections,
    loading,
    error,
    SUPPORTED_BROKERS,
    addBrokerIntegration,
    deleteBrokerIntegration,
    verifyBrokerConnection,
    refreshIntegrations
  } = useBrokerIntegration();

  const [showBrokerSelection, setShowBrokerSelection] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingBroker, setPendingBroker] = useState('');
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const navigate = useNavigate();
  
  // Zustand store for OAuth state management
  const { 
    startOAuthFlow, 
    setBrokerConnecting, 
    clearBrokerConnecting,
    getSandboxMode,
    getStorageManager 
  } = useBrokerOAuthStore();

  const handleConnectBroker = async (broker, isSandbox = false) => {
    if (!broker) return;
    
    // Check if user already has an active broker connection
    if (activeConnections && activeConnections.length > 0) {
      const currentBroker = activeConnections[0];
      console.log('User already has active broker:', currentBroker.broker);
      
      // Show warning modal
      setPendingBroker(broker);
      setShowWarningModal(true);
      return;
    }
    
    await proceedWithConnection(broker, isSandbox);
  };

  const proceedWithConnection = async (broker, isSandbox = false) => {
    setIsConnecting(true);
    
    try {
      console.log(`🔗 Initiating OAuth flow for ${broker}:`, { isSandbox });
      
      // Check storage compatibility before starting
      const storageInfo = getStorageManager().getStorageInfo();
      console.log('📊 Storage compatibility:', storageInfo);
      
      // Store broker type and sandbox preference using Zustand
      setBrokerConnecting(broker);
      
      // Use unified OAuth service for all brokers including Tastytrade
      // (Removed special case for Tastytrade as the login page doesn't exist)
      const oauthData = await unifiedOAuthService.getAuthorizationUrl(broker, isSandbox);
      
      // Start OAuth flow with state management
      startOAuthFlow(broker, oauthData.state, isSandbox);
      
      console.log(`✅ OAuth URL generated for ${broker}:`, {
        hasUrl: !!oauthData.url,
        hasState: !!oauthData.state,
        redirectUri: oauthData.redirectUri
      });
      
      // Redirect to broker OAuth page
      window.location.href = oauthData.url;
      
    } catch (error) {
      console.error(`❌ Error initiating broker connection for ${broker}:`, error);
      
      // Clean up state on error
      clearBrokerConnecting();
      setIsConnecting(false);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to connect to broker. ';
      
      if (error.message.includes('authentication')) {
        errorMessage += 'Please log in and try again.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage += 'Please check your internet connection and try again.';
      } else if (error.message.includes('Unsupported broker')) {
        errorMessage += 'This broker is not currently supported.';
      } else {
        errorMessage += 'Please try again or contact support if the issue persists.';
      }
      
      // You might want to show this error in a toast/notification
      console.error('User-facing error:', errorMessage);
      alert(errorMessage); // Replace with proper notification system
    }
  };

  const handleDisconnectAndConnect = async () => {
    if (!activeConnections || activeConnections.length === 0) return;

    setIsDisconnecting(true);
    setShowWarningModal(false);

    try {
      // Disconnect current broker
      const currentBroker = activeConnections[0];
      console.log('Disconnecting current broker:', currentBroker.broker);
      
      await deleteBrokerIntegration(currentBroker.id);
      
      // Wait a moment for the disconnect to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Proceed with new connection
      await proceedWithConnection(pendingBroker, false);
      
    } catch (error) {
      console.error('Error disconnecting broker:', error);
      alert('Failed to disconnect current broker. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCancelWarning = () => {
    setShowWarningModal(false);
    setPendingBroker('');
  };

  const handleDelete = async (integrationId) => {
    if (window.confirm('Are you sure you want to delete this broker integration?')) {
      try {
        await deleteBrokerIntegration(integrationId);
      } catch (error) {
        console.error('Error deleting integration:', error);
      }
    }
  };

  const handleVerify = async (integrationId) => {
    try {
      await verifyBrokerConnection(integrationId);
    } catch (error) {
      console.error('Error verifying connection:', error);
    }
  };


  const getBrokerDisplayName = (broker) => {
    const displayNames = {
      'tradier': 'Tradier',
      'tastytrade': 'TastyTrade',
      'schwab': 'Charles Schwab',
      'interactive_brokers': 'Interactive Brokers'
    };
    return displayNames[broker] || broker.charAt(0).toUpperCase() + broker.slice(1).replace('_', ' ');
  };

  const getBrokerIcon = (integration) => {
    // Check if expired
    const now = new Date();
    const expiresAt = integration.credentials?.expiresAt ? new Date(integration.credentials.expiresAt) : null;
    const sessionExpiration = integration.credentials?.sessionExpiration ? new Date(integration.credentials.sessionExpiration) : null;
    const expirationDate = expiresAt || sessionExpiration;
    
    if (expirationDate && expirationDate < now) {
      return <FiRefreshCw className="w-6 h-6 text-orange-500" />;
    }

    switch (integration.status) {
      case 'active':
        return <FiCheckCircle className="w-6 h-6 text-green-500" />;
      case 'error':
        return <FiAlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <FiAlertCircle className="w-6 h-6 text-yellow-500" />;
    }
  };

  if (loading) {
    return <div className="text-center text-gray-400">Loading broker integrations...</div>;
  }

  // Run storage compatibility check on component mount
  React.useEffect(() => {
    const storageInfo = getStorageManager().getStorageInfo();
    if (!storageInfo.available.sessionStorage && !storageInfo.available.localStorage) {
      console.warn('⚠️ Limited storage available - OAuth may have reduced functionality in private browsing mode');
    }
  }, []);

  return (
    <div className="bg-gray-900 rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-white">Broker Integrations</h2>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              setIsRefreshing(true);
              try {
                await refreshIntegrations();
              } catch (error) {
                console.error('Failed to refresh integrations:', error);
              } finally {
                setIsRefreshing(false);
              }
            }}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Refresh"
            disabled={isRefreshing}
          >
            <FiRefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              // Check storage compatibility before showing broker selection
              const storageInfo = getStorageManager().getStorageInfo();
              if (!storageInfo.available.sessionStorage && !storageInfo.available.localStorage) {
                alert('Warning: Limited storage available. OAuth may not work properly in private browsing mode.');
              }
              setShowBrokerSelection(!showBrokerSelection);
            }}
            className="flex items-center gap-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Connect Broker
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500 text-red-500 rounded-lg">
          {error}
        </div>
      )}

      {/* Broker Selection */}
      {showBrokerSelection && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg">
          <h3 className="text-lg font-medium text-white mb-4">Connect Your Broker Account</h3>
          <p className="text-gray-400 mb-4">
            Select a broker below to connect your account using secure OAuth authentication. 
            You'll be redirected to your broker's login page.
          </p>
          
          {/* Storage compatibility warning */}
          {(() => {
            const storageInfo = getStorageManager().getStorageInfo();
            if (!storageInfo.available.sessionStorage && !storageInfo.available.localStorage) {
              return (
                <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500 text-yellow-400 rounded-lg text-sm">
                  ⚠️ <strong>Limited Storage Detected:</strong> You appear to be in private browsing mode. 
                  OAuth authentication will use memory storage and may require re-authentication if you refresh the page.
                </div>
              );
            }
            return null;
          })()}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Supported Brokers */}
            {Object.entries(SUPPORTED_BROKERS).map(([key, value]) => {
              const colorClasses = {
                blue: 'bg-blue-500 hover:bg-blue-600',
                orange: 'bg-orange-500 hover:bg-orange-600',
                red: 'bg-red-500 hover:bg-red-600',
                green: 'bg-green-500 hover:bg-green-600',
                purple: 'bg-purple-500 hover:bg-purple-600'
              };

              return (
                <div key={value} className="p-4 bg-gray-700 rounded-lg border border-gray-600">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-white font-medium">{getBrokerDisplayName(value)}</h4>
                      <p className="text-gray-400 text-sm">Secure OAuth integration</p>
                    </div>
                    <span className="text-green-400 text-xs bg-green-400/10 px-2 py-1 rounded">Available</span>
                  </div>
                  
                  {/* Live Account Button */}
                  <button
                    onClick={() => handleConnectBroker(value, false)}
                    disabled={isConnecting}
                    className={`w-full flex items-center justify-center gap-2 ${colorClasses.blue} text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-2`}
                  >
                    {isConnecting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <FiExternalLink className="w-4 h-4" />
                        Connect Live Account
                      </>
                    )}
                  </button>
                  
                  {/* Sandbox Account Button (for supported brokers) */}
                  {value !== 'tastytrade' && (
                    <button
                      onClick={() => handleConnectBroker(value, true)}
                      disabled={isConnecting}
                      className={`w-full flex items-center justify-center gap-2 ${colorClasses.orange} text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm`}
                    >
                      {isConnecting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Connecting...
                        </>
                      ) : (
                        <>
                          <FiExternalLink className="w-3 h-3" />
                          Connect Sandbox Account
                        </>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={() => setShowBrokerSelection(false)}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}



      {/* Integrations List */}
      <div className="space-y-3 mt-6">
        {brokerIntegrations.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No broker integrations yet. Click "Connect Broker" to get started.
          </div>
        ) : (
          brokerIntegrations.map((integration) => (
            <div
              key={integration.id}
              className="flex items-center justify-between p-4 bg-gray-800 rounded-lg hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                  {getBrokerIcon(integration)}
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">
                    {getBrokerDisplayName(integration.broker)}
                  </h4>
                  <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                    {/* Account Information */}
                    <span>
                      Account: {integration.metadata?.primaryAccountNumber || 
                                integration.metadata?.selectedAccount || 
                                integration.balances?.[0]?.accountNumber ||
                                integration.credentials?.accountId || 
                                'Not Selected'}
                    </span>
                    
                    {/* Enhanced Profile Display */}
                    {integration.profile?.firstName && (
                      <span>
                        Holder: {integration.profile.firstName} {integration.profile.lastName || ''}
                      </span>
                    )}
                    
                    {/* Account Count */}
                    {integration.metadata?.totalAccounts > 1 && (
                      <span className="bg-blue-900 text-blue-300 px-2 py-0.5 rounded-full text-xs">
                        {integration.metadata.totalAccounts} accounts
                      </span>
                    )}
                    
                    {/* Balance Summary */}
                    {integration.balances?.length > 0 && integration.balances[0].accountValue && (
                      <span className="text-green-400">
                        ${new Intl.NumberFormat('en-US').format(integration.balances[0].accountValue)}
                      </span>
                    )}
                    
                    <BrokerStatusBadge integration={integration} />
                    
                    <span>Status: 
                      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        integration.accountStatus === 'active' 
                          ? 'bg-green-900 text-green-300' 
                          : integration.accountStatus === 'paused'
                          ? 'bg-yellow-900 text-yellow-300'
                          : 'bg-red-900 text-red-300'
                      }`}>
                        {integration.accountStatus === 'active' ? 'Active' : 
                         integration.accountStatus === 'paused' ? 'Paused' : 
                         integration.accountStatus === 'terminated' ? 'Terminated' : 'Active'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {!integration.metadata?.isVerified && integration.status === 'active' && (
                  <button
                    onClick={() => handleVerify(integration.id)}
                    className="p-2 text-blue-500 hover:text-blue-400 transition-colors"
                    title="Verify Connection"
                  >
                    <FiCheckCircle className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(integration.id)}
                  className="p-2 text-red-500 hover:text-red-400 transition-colors"
                  title="Delete Integration"
                >
                  <FiTrash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Loading Modal during OAuth connection */}
      {isConnecting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-8 max-w-md w-full mx-4 border border-gray-700">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-white mb-2">
                Connecting to Broker
              </h3>
              <p className="text-gray-400 mb-4">
                Please wait while we redirect you to your broker's authentication page...
              </p>
              <div className="space-y-2 text-sm text-gray-500">
                <p>✓ Generating secure OAuth state</p>
                <p>✓ Validating broker connection</p>
                <p>🔄 Redirecting to broker login...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      <BrokerConnectionWarningModal
        isOpen={showWarningModal}
        onClose={handleCancelWarning}
        onDisconnectAndConnect={handleDisconnectAndConnect}
        onCancel={handleCancelWarning}
        currentBroker={activeConnections?.[0]?.broker}
        newBroker={pendingBroker}
        isDisconnecting={isDisconnecting}
      />
    </div>
  );
};

export default BrokerIntegrationManager; 